<template>
  <UiComponentWrapper
    class="plan"
    :class="{ recommended: isRecommended }"
  >
    <UiComponentWrapper class="recommendation">
      <UiComponentParagraph>{{ props.wording.recommendedForYou }}</UiComponentParagraph>
      <UiComponentParagraph @click="emit('mac:recalculate:click')">
        ({{ props.wording.recalculate }})
      </UiComponentParagraph>
    </UiComponentWrapper>
    <UiComponentWrapper class="outer">
      <UiComponentWrapper
        ref="$discount"
        class="discount"
        :class="{
          disabled: props.billingMode === 'monthly',
          hoverable: !!save,
        }"
      >
        <UiComponentWrapper
          class="inner"
          :data-information="
            props.billingMode === 'monthly'
              ? $interpolate(props.plan.wording.saveXPercentInYearly, {
                '%{discount}': `${discount}%`,
              })
              : save
                ? $interpolate(props.wording.save, {
                  '%{amount}': save,
                })
                : undefined
          "
        >
          -{{ discount }}%
        </UiComponentWrapper>
      </UiComponentWrapper>

      <UiComponentWrapper class="head">
        <UiComponentHeading :level="2">
          {{ props.plan.title }}
        </UiComponentHeading>
        <UiComponentWrapper class="cost">
          <UiComponentParagraph class="price">
            {{ lastPrice }}
          </UiComponentParagraph>
          <UiComponentWrapper class="more">
            <UiComponentParagraph>{{ props.plan.wording.perMonth }}</UiComponentParagraph>
            <UiComponentParagraph>{{ billingType }}</UiComponentParagraph>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>

      <UiComponentHeading
        ref="$question"
        as="paragraph"
        class="question"
        :level="6"
      >
        {{ props.plan.text }}
      </UiComponentHeading>

      <UiComponentWrapper
        class="count"
        :data-information="props.wording.activeContacts"
      >
        <UiComponentHeading
          as="paragraph"
          :level="6"
        >
          {{
            $interpolate(props.plan.activeContactsLimit, {
              '%{activeContact}': activeContacts,
            })
          }}
        </UiComponentHeading>
        <UiComponentIcon
          type="info"
          @mouseover="onMouseOver"
          @mouseout="onMouseOut"
        />
      </UiComponentWrapper>

      <UiComponentWrapper
        ref="$input"
        class="range"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="336"
          height="80"
          viewBox="0 0 336 80"
          fill="none"
          preserveAspectRatio="none"
        >
          <path
            d="M331.169 0.325225C243.612 7.81982 260.291 63.7626 0 80H331.5C333.709 80 335.5 78.2091 335.5 76V4.26787C335.5 1.95685 333.471 0.12813 331.169 0.325225Z"
            fill="url(#paint0_linear_4504_38466)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_4504_38466"
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
              gradientUnits="userSpaceOnUse"
            >
              <stop
                offset="0"
                stop-opacity="0.3"
                :stop-color="animatedBackgroundColor"
              />
              <stop
                offset="1"
                stop-opacity="0.1"
                :stop-color="animatedBackgroundColor"
              />
            </linearGradient>
          </defs>
        </svg>
        <UiComponentWrapper
          class="mask"
          :style="{ width: `${100-progress}%` }"
        />
        <UiComponentWrapper class="track">
          <UiComponentWrapper
            class="progress"
            :style="{ width: `${progress}%` }"
          />
          <UiComponentWrapper
            v-for="(stop, index) in stops"
            :key="`stop-${stop}`"
            class="stop"
            :class="{
              active: progress >= stop,
              disabled: index > maxIndex,
            }"
            :style="{ left: `${stop}%` }"
            @click="moveTo(stop)"
          >
            <UiComponentWrapper
              class="inner"
              :data-information="props.plan.wording.yearlyBillingOnly"
            />
          </UiComponentWrapper>
          <UiComponentWrapper
            ref="$thumb"
            class="thumb"
            :class="{ dragging }"
            :style="{ left: `${progress}%` }"
          >
            <UiComponentWrapper
              v-for="n in 3"
              :key="`line-${n}`"
              class="line"
            />
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>

      <AtomButton
        :action="props.plan.cta.action"
        :label="`Column ${props.plan.title} CTA`"
        :to="to"
        size="l"
      >
        {{ props.plan.cta.slot }}
      </AtomButton>

      <UiComponentWrapper class="usage">
        <UiComponentHeading :level="3">
          {{ props.wording.usage }}
        </UiComponentHeading>
        <UiComponentWrapper class="list">
          <UiComponentWrapper
            v-for="{ id, icon, text, information } in props.plan.usage"
            :key="`business-plan-usage-${id}`"
            class="feature"
            :class="{ disabled: icon.name === 'x' }"
            :data-information="information"
          >
            <UiComponentIcon :type="icon.name" />
            <UiComponentHeading
              as="paragraph"
              :level="6"
            >
              {{ $interpolate(text, {
                '%{currentActiveContacts}': activeContacts,
              }) }}
            </UiComponentHeading>
            <UiComponentIcon
              v-if="information"
              type="info"
              @mouseover="onMouseOver"
              @mouseout="onMouseOut"
            />
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>

      <UiComponentWrapper class="add-ons">
        <UiComponentHeading :level="3">
          {{ props.wording.addons }}
        </UiComponentHeading>
        <UiComponentWrapper class="list">
          <UiComponentWrapper
            v-for="{ id, icon, text, information } in props.plan.addons"
            :key="`business-plan-add-on-${id}`"
            class="feature"
            :class="{ disabled: icon.name === 'x' }"
            :data-information="information"
          >
            <UiComponentIcon :type="icon.name" />
            <UiComponentHeading
              as="paragraph"
              :level="6"
            >
              {{ text }}
            </UiComponentHeading>
            <UiComponentIcon
              v-if="information"
              type="info"
              @mouseover="onMouseOver"
              @mouseout="onMouseOut"
            />
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>

      <UiComponentWrapper class="integrations">
        <UiComponentHeading :level="3">
          {{ props.wording.integrations }}
        </UiComponentHeading>
        <UiComponentWrapper class="list">
          <UiComponentParagraph>{{ props.wording.baseIntegrations }}</UiComponentParagraph>
          <UiComponentWrapper class="details">
            <UiComponentWrapper
              v-for="{ id, name, logo } in props.plan.integrations"
              :key="`business-plan-integration-${id}`"
              class="integration"
              :data-name="name"
            >
              <UiComponentImage
                :alt="logo.alt"
                class="logo"
                :expected-size="20"
                :height="logo.height"
                :source="logo.url"
                :width="logo.width"
              />
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>

      <UiComponentWrapper class="services">
        <UiComponentHeading :level="3">
          {{ props.wording.services }}
        </UiComponentHeading>
        <UiComponentWrapper class="list">
          <UiComponentWrapper
            v-for="{ id, icon, text, information } in props.plan.services"
            :key="`business-plan-service-${id}`"
            class="feature"
            :class="{ disabled: icon.name === 'x' }"
            :data-information="information"
          >
            <UiComponentIcon :type="icon.name" />
            <UiComponentHeading
              as="paragraph"
              :level="6"
            >
              {{ text }}
            </UiComponentHeading>
            <UiComponentIcon
              v-if="information"
              type="info"
              @mouseover="onMouseOver"
              @mouseout="onMouseOut"
            />
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>

      <UiComponentWrapper class="separator" />

      <UiComponentWrapper class="used-by">
        <UiComponentHeading :level="3">
          {{ props.wording.usedBy }}
        </UiComponentHeading>
        <UiComponentWrapper class="list">
          <UiComponentImage
            v-for="{ id, logo } in props.plan.usedBy"
            :key="`business-plan-used-by-${id}`"
            :alt="logo.alt"
            class="logo"
            :expected-size="24"
            :extra-params="{
              con: 100,
              gam: 100,
              monochrome: '000000',
              sat: -100,
              trim: 'color',
              vib: 100,
            }"
            :height="logo.height"
            :orientation="logo.width >= logo.height ? 'portrait' : 'landscape'"
            :source="logo.url"
            use-natural-height
            use-natural-width
            :width="logo.width"
          />
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentWrapper>
  </UiComponentWrapper>
</template>

<script setup>
// Imports
import { gsap } from 'gsap'

// Composables
const { isLogged } = useIsLoggedIn()
const { $interpolate } = useNuxtApp()

// Emit
const emit = defineEmits([
  'mac:change',
  'mac:definition:click',
  'mac:exceeded',
  'mac:recalculate:click',
  'mac:slider:interacted',
])

// Props
const props = defineProps({
  billingMode: {
    required: true,
    type: String,
  },
  currency: {
    required: true,
    type: String,
  },
  isRecommended: {
    required: false,
    type: Boolean,
  },
  plan: {
    required: true,
    type: Object,
  },
  wording: {
    required: true,
    type: Object,
  },
})

// Refs
const $discount = ref(null)
const $question = ref(null)
const $input = ref(null)
const $thumb = ref(null)
const animatedActiveContacts = ref(null)
const animatedBackgroundColor = ref('#FED348')
const animatedDiscount = ref(null)
const discountColors = ref([
  {
    backgroundColor: '#FED348',
    textColor: '#232B2F',
  },
  {
    backgroundColor: '#FFC91A',
    textColor: '#232B2F',
  },
  {
    backgroundColor: '#85E0D1',
    textColor: '#232B2F',
  },
  {
    backgroundColor: '#59D4C0',
    textColor: '#232B2F',
  },
  {
    backgroundColor: '#17A679',
    textColor: '#FFFFFF',
  },
  {
    backgroundColor: '#0C7C59',
    textColor: '#FFFFFF',
  },
])
const dragging = ref(false)
const lastPrice = ref(`79${props.currency}`)
const progress = ref(0)

// Computed Properties
const activeContacts = computed(() => {
  return props.plan.options[currentIndex.value].activeContactsLimit
})

const billingType = computed(() => {
  const key = `billed${capitalize(props.billingMode)}`
  return props.plan.wording[key]
})

const currentIndex = computed(() => {
  const goal = Number.parseInt(progress.value)
  const closest = stops.value.reduce((accumulator, current) => {
    return (Math.abs(current - goal) < Math.abs(accumulator - goal) ? current : accumulator)
  })
  return stops.value.findIndex(value => closest === value)
})

const discount = computed(() => {
  return props.plan.options[currentIndex.value].discount
})

const maxIndex = computed(() => {
  const key = `priceWhen${capitalize(props.billingMode)}Billed`
  const { length } = props.plan.options
    .filter(option => !!option[key])
  return length - 1
})

const price = computed(() => {
  const key = `priceWhen${capitalize(props.billingMode)}Billed`
  return props.plan.options[currentIndex.value][key]
})

const save = computed(() => {
  if (!props.plan.options[currentIndex.value].priceWhenMonthlyBilled) {
    return undefined
  }

  const amount = (props.plan.options[currentIndex.value].priceWhenMonthlyBilled * 12) - (props.plan.options[currentIndex.value].priceWhenYearlyBilled * 12)

  return getFormattedPrice(amount)
})

const stops = computed(() => {
  const { length } = props.plan.options
  return Array.from({ length })
    .map((_, index) => (100 / (length - 1)) * index)
})

const to = computed(() => {
  // const queryParameters = new URLSearchParams({
  //   active_contact_limit: activeContacts.value,
  //   cycle: props.billingMode,
  // })
  // return isLogged
  //   ? `${props.plan.cta.to}&${queryParameters.toString()}`
  //   : props.plan.cta.to
  return props.plan.cta.to
})

// Watchers
watch(
  () => activeContacts.value,
  (newValue, oldValue) => {
    const buffer = {
      value: oldValue,
    }

    gsap.to(
      buffer,
      {
        duration: 0.6,
        ease: 'none',
        onUpdate() {
          animatedActiveContacts.value = Number.parseInt(buffer.value)
        },
        value: newValue,
      },
    )
  },
)

watch(
  () => currentIndex.value,
  (value) => {
    gsap.set($discount.value.$el.querySelector('.inner'), {
      backgroundColor: discountColors.value[value].backgroundColor,
      color: discountColors.value[value].textColor,
      overwrite: true,
    })

    gsap.set(animatedBackgroundColor, {
      overwrite: true,
      value: discountColors.value[value].backgroundColor,
    })

    if (getFormattedPrice(price.value)) {
      lastPrice.value = getFormattedPrice(price.value)
    }
  },
)

watch(
  () => discount.value,
  (newValue, oldValue) => {
    const buffer = {
      value: oldValue,
    }

    gsap.to(
      buffer,
      {
        duration: 0.6,
        ease: 'none',
        onUpdate() {
          animatedDiscount.value = Number.parseInt(buffer.value)
        },
        value: newValue,
      },
    )
  },
)

watch(
  () => props.billingMode,
  () => {
    const buffer = {
      value: stops.value
        .filter((_, index) => index <= maxIndex.value)
        .reduce((accumulator, current) => {
          return (Math.abs(current - progress.value) < Math.abs(accumulator - progress.value) ? current : accumulator)
        }),
    }

    if (buffer.value !== progress.value) {
      gsap.from(
        buffer,
        {
          duration: 0.6,
          ease: 'none',
          onUpdate() {
            progress.value = buffer.value
          },
          value: progress.value,
        },
      )
    }

    if (getFormattedPrice(price.value)) {
      lastPrice.value = getFormattedPrice(price.value)
    }
  },
)

watch(
  () => props.currency,
  () => {
    if (getFormattedPrice(price.value)) {
      lastPrice.value = getFormattedPrice(price.value)
    }
  },
)

// Methods
function bindEvents() {
  const dragStart = 'ontouchstart' in document ? 'touchstart' : 'mousedown'
  const drag = 'ontouchmove' in document ? 'touchmove' : 'mousemove'

  $question.value.$el.querySelector('strong').addEventListener('click', onClick)
  $thumb.value.$el.addEventListener(dragStart, onDragStart)
  document.addEventListener(drag, onDrag, { passive: true })
}

function capitalize([firstLetter, ...restOfWord]) {
  return firstLetter.toUpperCase() + restOfWord.join('')
}

function getFormattedPrice(value) {
  const parsedValue = Number.parseInt(value)

  if (Number.isNaN(parsedValue)) {
    return false
  }

  const content = []
  if (props.currency === '$') {
    content.push('$')
    content.push(parsedValue.toLocaleString('en-US'))
  }

  if (props.currency === '€') {
    content.push(parsedValue)
    content.push('€')
  }

  return content.join('')
}

function moveTo(value) {
  const max = Math.max(
    ...stops.value
      .filter((_, index) => index <= maxIndex.value),
  )
  if (value > max) {
    emit('mac:exceeded')
  }

  gsap.to(progress, {
    duration: 1,
    ease: 'power4.inOut',
    onStart() {
      emit('mac:slider:interacted')
    },
    onComplete() {
      nextTick(() => {
        emit('mac:change', activeContacts.value)
      })
    },
    value,
  })
}

function onClick() {
  emit('mac:definition:click')
}

function onDragEnd() {
  const dragEnd = 'ontouchend' in document ? 'touchend' : 'mouseup'
  document.removeEventListener(dragEnd, onDragEnd)

  dragging.value = false
  progress.value = stops.value
    .filter((_, index) => index <= maxIndex.value)
    .reduce((accumulator, current) => {
      return (Math.abs(current - progress.value) < Math.abs(accumulator - progress.value) ? current : accumulator)
    })

  nextTick(() => {
    emit('mac:change', activeContacts.value)
  })
}

function onDragStart() {
  const dragEnd = 'ontouchend' in document ? 'touchend' : 'mouseup'
  document.addEventListener(dragEnd, onDragEnd)

  dragging.value = true

  emit('mac:slider:interacted')
}

function onDrag(event) {
  if (!dragging.value) {
    return
  }

  const { clientX } = event.type === 'touchmove' ? event.touches[0] : event
  const { x, width } = $input.value.$el.getBoundingClientRect()
  const offset = x - clientX
  const min = 0
  const [max] = [...stops.value]
    .filter((_, index) => index <= maxIndex.value)
    .reverse()

  if (offset >= min) {
    progress.value = min
  }
  else if (Math.abs(offset) >= width * (max * 0.01)) {
    progress.value = max
  }
  else {
    const value = (Math.abs(offset) / width) * 100
    progress.value = value
  }
}

function onMouseOver({ currentTarget }) {
  const { parentNode } = currentTarget
  parentNode.style.setProperty('--left', `${currentTarget.getBoundingClientRect().x - parentNode.getBoundingClientRect().x + 8}px`)
  parentNode.classList.add('hoverable')
}

function onMouseOut({ currentTarget }) {
  currentTarget.parentNode.classList.remove('hoverable')
}

function unbindEvents() {
  const dragStart = 'ontouchstart' in document ? 'touchstart' : 'mousedown'
  const drag = 'ontouchmove' in document ? 'touchmove' : 'mousemove'

  $question.value?.$el?.querySelector('strong')?.removeEventListener('click', onClick)
  $thumb.value?.$el?.removeEventListener(dragStart, onDragStart)
  document.removeEventListener(drag, onDrag)
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  unbindEvents()
})

onBeforeMount(() => {
  animatedActiveContacts.value = activeContacts.value
  animatedDiscount.value = discount.value
  lastPrice.value = getFormattedPrice(price.value)
})

onMounted(() => {
  bindEvents()
})
</script>

<style lang="postcss" scoped>
.plan {
  @apply relative;

  &.recommended {
    &::before {
      @apply absolute -bottom-px -left-px -right-px -top-7 bg-blizzard-700 content-[''] rounded-3xl;
    }

    .recommendation {
      @apply opacity-100 visible;
    }

    .outer {
      @apply border-blizzard-700;
    }
  }

  .recommendation {
    @apply absolute -left-px -right-px -top-7 justify-center gap-1 opacity-0 invisible;

    p {
      @apply font-medium text-center text-white text-xs leading-7;

      & + p {
        @apply font-normal underline underline-offset-2 cursor-pointer;
      }
    }
  }

  .outer {
    @apply flex-col p-6 pb-8 bg-white border-2 border-grey-blue-200 rounded-3xl z-10;

    @screen md {
      @apply p-8;
    }

    .discount {
      @apply absolute right-0 top-7 w-auto;

      @screen md {
        @apply right-8 top-9;
      }

      @screen xl {
        @apply right-0;
      }

      &.disabled {
        .inner {
          @apply bg-grey-blue-200 text-grey-blue-400 line-through !important;
        }
      }

      &.hoverable {
        .inner {
          &:hover {
            &::after,
            &::before {
              @apply opacity-100 visible;
            }
          }
        }
      }

      .inner {
        @apply relative w-auto p-2 bg-[#FED348] font-bold text-[#232B2F] text-[13px] leading-[17px] rounded-xs transform translate-x-3 transition-colors duration-600;

        @screen md {
          @apply translate-x-0;
        }

        @screen xl {
          @apply translate-x-1/2;
        }

        &::after {
          @screen xl {
            @apply absolute left-1/2 -top-3 h-5 w-5 bg-grey-blue-100 rounded-md transform origin-center -translate-x-1/2 -translate-y-3/4 rotate-45 opacity-0 invisible transition-all duration-500 ease-in-out content-[''] z-20;
          }
        }

        &::before {
          @screen xl {
            @apply absolute left-1/2 -top-3 w-max px-6 py-[14px] bg-grey-blue-100 font-normal text-winter-green-900 text-center text-sm leading-6 rounded shadow-light4 transform -translate-x-1/2 -translate-y-full opacity-0 invisible transition-all duration-500 ease-in-out content-[attr(data-information)] z-20;
          }
        }
      }
    }

    .head {
      @apply items-center justify-between h-10;

      .h2 {
        @apply font-medium text-2xl leading-8;

        @screen mdminus {
          @apply text-[28px] leading-[34px];
        }

        @screen mdplus {
          @apply text-3xl leading-10;
        }
      }

      .cost {
        @apply items-center gap-[7px] w-auto mr-6;

        @screen md {
          @apply mr-15;
        }

        @screen xl {
          @apply mr-0;
        }

        .price {
          @apply font-medium text-livestorm-blue-700 text-3xl leading-none;
        }

        .more {
          @apply flex-col w-auto;

          p {
            @apply text-winter-green-900 text-[13px] leading-[17px];
          }
        }
      }
    }

    .question {
      @apply mt-5 font-normal text-grey-blue-600 text-sm leading-6 z-10;

      :deep(strong) {
        @apply text-grey-blue-600 underline cursor-pointer;
      }
    }

    .count {
      @apply relative items-center gap-1 h-8 mt-[18px] z-10;

      &::after {
        @apply absolute left-[var(--left)] top-0 h-5 w-5 bg-grey-blue-100 rounded-md transform origin-center -translate-x-1/2 -translate-y-3/4 rotate-45 opacity-0 invisible transition-all duration-500 ease-in-out content-[''];
      }

      &::before {
        @apply absolute top-0 px-6 py-[14px] bg-grey-blue-100 text-winter-green-900 text-center text-sm leading-6 rounded shadow-light4 transform -translate-y-full opacity-0 invisible transition-all duration-500 ease-in-out content-[attr(data-information)];
      }

      &.hoverable {
        &::after,
        &::before {
          @apply opacity-100 visible;
        }
      }

      p {
        @apply font-normal text-grey-blue-600 text-sm leading-6;

        :deep(strong) {
          @apply font-bold text-livestorm-blue-600 text-sm leading-6;
        }
      }

      .icon {
        @apply text-grey-blue-600 text-base leading-none;
      }
    }

    .range {
      @apply relative mt-3 z-0;

      svg {
        @apply absolute top-0 h-20 w-full transform -translate-y-full z-0;
      }

      .mask {
        @apply absolute right-0 top-0 h-20 w-full bg-white transform -translate-y-full z-10;
      }

      .track {
        @apply relative h-0.5 bg-grey-blue-300 z-20;

        .progress {
          @apply absolute left-0 top-0 w-0 h-full bg-livestorm-blue-600;
        }

        .stop {
          @apply absolute left-0 top-px h-2 w-2 bg-grey-blue-300 transform -translate-x-1/2 -translate-y-1/2 rounded-sm cursor-pointer;

          &.active {
            @apply bg-livestorm-blue-600;
          }

          &.disabled {
            &:hover {
              .inner {
                &::after,
                &::before {
                  @apply opacity-100 visible;
                }
              }
            }
          }

          .inner {
            @apply relative;

            &::after {
              @apply absolute left-1/2 -top-3 h-5 w-5 bg-grey-blue-100 rounded-md transform origin-center -translate-x-1/2 -translate-y-3/4 rotate-45 opacity-0 invisible transition-all duration-500 ease-in-out content-[''] z-10;
            }

            &::before {
              @apply absolute left-1/2 -top-3 w-max px-6 py-[14px] bg-grey-blue-100 text-winter-green-900 text-center text-sm leading-6 rounded shadow-light4 transform -translate-x-1/2 -translate-y-full opacity-0 invisible transition-all duration-500 ease-in-out content-[attr(data-information)] z-10;
            }
          }
        }

        .thumb {
          @apply absolute left-0 top-px items-center justify-center gap-0.5 h-4 w-5 bg-white border border-grey-blue-300 transform -translate-x-1/2 -translate-y-1/2 rounded-md cursor-grab;

          &.dragging {
            @apply cursor-grabbing;
          }

          .line {
            @apply h-1 w-px bg-grey-blue-300;
          }
        }
      }
    }

    .button {
      @apply mt-5;
    }

    .usage,
    .add-ons,
    .services {
      @apply flex-col gap-2 mt-6;

      .h3 {
        @apply text-sm leading-6;
      }

      .list {
        @apply flex-col gap-2;

        .feature {
          @apply relative inline-flex items-start gap-1;

          &::after {
            @apply absolute left-[var(--left)] -top-3 h-5 w-5 bg-grey-blue-100 rounded-md transform origin-center -translate-x-1/2 -translate-y-3/4 rotate-45 opacity-0 invisible transition-all duration-500 ease-in-out content-[''];
          }

          &::before {
            @apply absolute -top-3 px-6 py-[14px] bg-grey-blue-100 text-winter-green-900 text-center text-sm leading-6 rounded shadow-light4 transform -translate-y-full opacity-0 invisible transition-all duration-500 ease-in-out content-[attr(data-information)];
          }

          &.disabled {
            .icon {
              @apply text-grey-blue-300 !important;

              &.icon-info {
                @apply text-grey-blue-400 !important;
              }
            }

            p {
              @apply text-grey-blue-400;
            }
          }

          &.hoverable {
            &::after,
            &::before {
              @apply opacity-100 visible;
            }
          }

          .icon {
            @apply text-grey-blue-600 text-base leading-none transform translate-y-1 !important;

            &.icon-check,
            &.icon-plus {
              @apply font-bold text-livestorm-blue-700 !important;
            }
          }

          p {
            @apply ml-1 font-normal text-grey-blue-600 text-sm leading-6;

            :deep(strong) {
              @apply text-grey-blue-800;
            }
          }
        }
      }
    }

    .integrations {
      @apply flex-col gap-2 mt-6;

      .h3 {
        @apply text-sm leading-6;
      }

      .list {
        @apply flex-wrap gap-2;

        p {
          @apply pt-2 text-grey-blue-800 text-sm leading-6;
        }

        .details {
          @apply gap-1 w-auto p-2 bg-grey-blue-200 rounded;

          .integration {
            @apply relative;

            &::after {
              @apply absolute left-1/2 -top-3 h-5 w-5 bg-grey-blue-100 rounded-md transform origin-center -translate-x-1/2 -translate-y-3/4 rotate-45 opacity-0 invisible transition-all duration-500 ease-in-out content-[''];
            }

            &::before {
              @apply absolute left-1/2 -top-3 px-6 py-[14px] bg-grey-blue-100 text-winter-green-900 text-center text-sm leading-6 rounded shadow-light4 transform -translate-x-1/2 -translate-y-full opacity-0 invisible transition-all duration-500 ease-in-out content-[attr(data-name)];
            }

            &:hover {
              &::after,
              &::before {
                @apply opacity-100 visible;
              }
            }

            .logo {
              @apply rounded-none;
            }
          }
        }
      }
    }

    .separator {
      @apply border-t-2 border-grey-blue-200 mt-6;
    }

    .used-by {
      @apply flex-col gap-2 mt-6;

      .h3 {
        @apply font-normal text-grey-blue-600 text-sm leading-6;
      }

      .list {
        @apply flex-nowrap gap-10;

        .logo {
          @apply rounded-none;

          :deep(img) {
            filter: invert(45%) sepia(13%) saturate(601%) hue-rotate(163deg)
              brightness(87%) contrast(84%);
          }
        }
      }
    }
  }
}
</style>
